import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';
import type { SourceRole } from '@aica/api-types/schemas';

interface ProcessingToggleBannerProps {
  /** Включён ли автоматический анализ у организации. При true баннер не рисуется вовсе. */
  enabled: boolean;
  /** Сколько записей уже принято — доказательство, что интеграция жива. */
  records: number;
  /**
   * Подключённый источник, `null` — не подключено ничего.
   *
   * `undefined` означает «сводка не знает»: ответ, собранный до появления поля,
   * мог пережить деплой в кеше react-query. Тогда баннер не утверждает об
   * источнике ничего — ни что он есть, ни что его нет.
   *
   * `role` отличает источник записей от установки телефонии в роли источника
   * аудио. Второй записей не создаёт вовсе, поэтому «подключён — ждём первые
   * записи» про него — неправда, и худшая из возможных: он выходит на первое
   * место ровно тогда, когда CRM перестала обслуживать организацию.
   * `undefined` (старый кеш) читается как источник записей — прежнее
   * поведение.
   */
  source?: { name: string; role?: SourceRole | null } | null;
  /** Идёт запрос на смену режима: кнопка блокируется, чтобы не отправить два. */
  isEnabling: boolean;
  /**
   * Есть ли у пользователя право переключить режим. Без него баннер остаётся
   * информирующим: агент и супервизор должны понимать, в каком режиме сейчас
   * анализ, но кнопку им показывать нечестно — сервер ответит 403.
   */
  canStart: boolean;
  onSetMode: (mode: 'auto' | 'manual') => void;
  /**
   * Причина паузы, если ручной режим поставил не человек, и когда это
   * случилось. `undefined` — сводка не знает (ответ из кеша, собранный до
   * появления полей): тогда баннер про паузу молчит, как молчал раньше.
   */
  pausedReason?: string | null;
  pausedAt?: string | null;
  /**
   * Может ли организация платить ПРЯМО СЕЙЧАС (вердикт биллингового гейта на
   * момент ответа сервера).
   *
   * Причина паузы — след прошлого и переживает пополнение намеренно: режим
   * возвращает только человек. Ложным в ней было ВРЕМЯ: «Закончились
   * средства» в настоящем времени читал и клиент, который уже пополнил
   * кошелёк, и та же организация в следующем цикле, со свежим грантом (#489).
   *
   * `undefined`/`null` — сервер не сказал (старый кеш или сбой биллинга):
   * тогда текст остаётся прежним, а не заявляет, что средства появились.
   */
  pausedFundsAvailable?: boolean | null;
}

/**
 * Баннер режима анализа — что именно он говорит, зависит от того, что
 * происходит на самом деле.
 *
 * Выключенный тумблер — не поломка: это ручной режим, в котором записи ждут
 * запуска (по одной или пачкой) и не тратят минут, пока их не запустили сами.
 * Раньше текст был другим: «приём звонков работает: получено записей — N» и
 * требование включить. У только что зарегистрированной организации это была
 * ложь дважды — приём не работал (источник не подключён), а N равнялось
 * нулю. Клиент видел «работает» одновременно с баннером «подключите
 * источник» этажом выше.
 *
 * Презентационный по построению: данные приходят пропсами, потому что в
 * портале их даёт `useProcessingState` (Clerk), а в Bitrix24 —
 * `useEmbeddedProcessingState` (cookie-сессия), и вызвать первый внутри
 * iframe нельзя — там нет ни Clerk, ни Role-контекста. Источник едет в той же
 * сводке бэклога по той же причине: `/sources/active` в embedded не
 * смонтирован, а текст обязан быть одинаково правдив в обеих средах.
 */
export function ProcessingToggleBanner({
  enabled,
  records,
  source,
  isEnabling,
  canStart,
  onSetMode,
  pausedReason,
  pausedAt,
  pausedFundsAvailable,
}: ProcessingToggleBannerProps) {
  const { t } = useTranslation('records');

  if (enabled) return null;

  // Пауза по нулю баланса перекрывает шкалу уверенности ниже. Та отвечает на
  // вопрос «работает ли приём»; здесь же приём точно работал — записи есть,
  // минуты кончились, — и первый вопрос клиента другой: почему всё встало.
  //
  // Порядок остальных веток сохранён как был: `records > 0` остаётся впереди
  // `audioOnly`/`noSource` (принятые записи — доказательство сильнее
  // конфигурации источника).
  const state = pausedReason
    ? 'paused'
    : records > 0
      ? 'received'
      : source?.role === 'audio_source'
        ? 'audioOnly'
        : source
          ? 'waiting'
          : source === null
            ? 'noSource'
            : 'unknown';

  // Название состояния («Анализ на паузе с 12 августа») остаётся верным и
  // после пополнения: режим по-прежнему ручной, и с той самой даты. Ложным
  // было ТЕЛО — «Закончились средства» в настоящем времени. Поэтому здесь
  // подменяется только оно, и только когда сервер прямо сказал, что платить
  // есть чем; `undefined`/`null` оставляют прежний текст.
  //
  // Ключи — литералами внутри `t()`, а не вычисленной строкой: их разрешение в
  // четырёх локалях проверяет `check:i18n-resolution`, и он видит только
  // литеральные вызовы. Собранный из кусков ключ прошёл бы гейт с опечаткой.
  const funded = pausedFundsAvailable === true;
  const pausedBody = (): string => {
    if (!canStart) {
      return funded
        ? t('records.processingOff.pausedFundedBodyReadOnly')
        : t('records.processingOff.pausedBodyReadOnly');
    }
    if (records === 0) {
      return funded
        ? t('records.processingOff.pausedFundedBodyEmpty')
        : t('records.processingOff.pausedBodyEmpty');
    }
    return funded
      ? t('records.processingOff.pausedFundedBody', { count: records })
      : t('records.processingOff.pausedBody', { count: records });
  };

  const copy = {
    paused: {
      title: pausedAt
        ? t('records.processingOff.pausedTitle', { date: formatDate(pausedAt) })
        : t('records.processingOff.pausedTitleNoDate'),
      // Три тела на одно название состояния — читатель либо не может нажать
      // кнопку вовсе (canStart===false — читатель узнаёт, ЧТО случилось, но
      // не получает совет, который сервер бы всё равно отклонил 403), либо
      // может, и тогда важно, есть ли что запускать: раньше здесь всегда
      // стоял счётчик и обещание «ждут запуска», а после того как клиент сам
      // разобрал накопленное и остался в ручном режиме — 0 разговоров ждут
      // ничего, и баннер лгал дважды. Шесть — потому что у каждого из трёх
      // есть вариант «средства уже есть».
      body: pausedBody(),
    },
    received: {
      title: t('records.processingOff.title', { records }),
      body: t('records.processingOff.body'),
    },
    waiting: {
      title: t('records.processingOff.waitingTitle', { source: source?.name }),
      body: t('records.processingOff.waitingBody'),
    },
    noSource: {
      title: t('records.processingOff.noSourceTitle'),
      body: t('records.processingOff.noSourceBody'),
    },
    // Источник аудио подключён — говорить «не подключено ничего» здесь так же
    // неверно, как говорить «приём работает». Действие при этом то же самое:
    // не хватает именно источника записей.
    audioOnly: {
      title: t('records.processingOff.audioOnlyTitle', { source: source?.name }),
      body: t('records.processingOff.audioOnlyBody'),
    },
    unknown: {
      title: t('records.processingOff.disabledTitle'),
      body: t('records.processingOff.disabledBody'),
    },
  }[state];

  return (
    <div className="mb-4 rounded-card border border-border bg-bg-elevated p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-medium text-fg">{copy.title}</p>
          <p className="mt-1 text-sm text-fg-muted">{copy.body}</p>
        </div>
        {/* Пока источника нет, переключать режим не то действие, которого
            ждут: включать обработку нечего. Ведём туда, где подключают. */}
        {state === 'noSource' || state === 'audioOnly' ? (
          <a
            href="/settings?section=data_source"
            className="shrink-0 rounded-button bg-primary px-3 py-2 text-sm font-medium text-bg transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('records.processingOff.noSourceAction')}
          </a>
        ) : (
          canStart && (
            <Button onClick={() => onSetMode('auto')} disabled={isEnabling}>
              {t('records.processingOff.action')}
            </Button>
          )
        )}
      </div>
    </div>
  );
}
